import React from "react";
import { Autocomplete, Box, TextField, useMediaQuery, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { IconButton } from "@mui/material";
import themestyle from "../theme";

export interface PassesToolBarProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  passTypeValue: string | null;
  onPassTypeChange: (val: string | null) => void;
  instructorName: string | null;
  onInstructorChange: (val: string | null) => void;
  instructorsList: string[];
}

const PASS_TYPES = [
  { label: "Style Based", value: "Style Based" },
  { label: "Choreography", value: "Choreography" },
  { label: "Krump", value: "Krump" },
];

const PassesToolBar: React.FC<PassesToolBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  passTypeValue,
  onPassTypeChange,
  instructorName,
  onInstructorChange,
  instructorsList,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [localSearch, setLocalSearch] = React.useState(searchQuery);
  React.useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearchQueryChange(localSearch);
    }
  };
  const handleSearchClick = () => {
    onSearchQueryChange(localSearch);
  };
  const handleClearSearch = () => {
    setLocalSearch("");
    onSearchQueryChange("");
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        gap: 2,
        mb: 3,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ flex: 1, minWidth: isMobile ? "100%" : "300px", maxWidth: isMobile ? "100%" : "600px" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search passes..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearchClick} size="small" sx={{ p: 0.5 }}>
                  <SearchIcon sx={{ color: "#9CA3AF" }} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: localSearch ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small" sx={{ p: 0.5 }}>
                  <ClearIcon sx={{ color: "#9CA3AF", fontSize: "18px" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "24px",
              backgroundColor: "#fff",
              height: "44px",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              "& fieldset": { borderColor: "#E5E7EB" },
              "&:hover fieldset": { borderColor: themestyle.colors.activeGreen },
              "&.Mui-focused fieldset": { borderColor: themestyle.colors.activeGreen },
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}>
        <Autocomplete
          size="small"
          options={PASS_TYPES}
          getOptionLabel={(option) => option.label}
          value={PASS_TYPES.find((type) => type.value === passTypeValue) || null}
          onChange={(event, newValue) => onPassTypeChange(newValue ? newValue.value : null)}
          disableClearable={false}
          sx={{ minWidth: isMobile ? "48%" : "200px", flex: isMobile ? 1 : "none" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Type"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  backgroundColor: "#fff",
                  height: "44px",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: themestyle.colors.activeGreen },
                  "&.Mui-focused fieldset": { borderColor: themestyle.colors.activeGreen },
                },
              }}
            />
          )}
        />
        <Autocomplete
          size="small"
          options={instructorsList}
          value={instructorName}
          onChange={(event, newValue) => onInstructorChange(newValue)}
          disableClearable={false}
          sx={{ minWidth: isMobile ? "48%" : "200px", flex: isMobile ? 1 : "none" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Instructor"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  backgroundColor: "#fff",
                  height: "44px",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: themestyle.colors.activeGreen },
                  "&.Mui-focused fieldset": { borderColor: themestyle.colors.activeGreen },
                },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};
export default PassesToolBar;
